import React from "react";
import { CardContent } from "@/components/ui/card";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import DashboardCardHeader from "@/components/common/DashboardCardHeader";
import { Typography } from "@/components/ui/typography";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import SDOHIconLight from "@/assets/icons/sdohIconLight.svg";
import SDOHIconDark from "@/assets/icons/sdohIconDark.svg";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface SDOHBarriersData {
  educationLiteracy: boolean;
  employment: boolean;
  housingShelter: boolean;
  transportation: boolean;
  foodResources: boolean;
  financial: boolean;
  safetyConcerns: boolean;
}

interface SDOHBarriersResponse {
  memberId: string;
  barriers: SDOHBarriersData;
}

const barrierLabels: Record<keyof SDOHBarriersData, string> = {
  educationLiteracy: "Education/Literacy",
  employment: "Employment",
  housingShelter: "Housing Shelter",
  transportation: "Transportation",
  foodResources: "Food Resources",
  financial: "Financial",
  safetyConcerns: "Safety Concerns",
};

interface SDOHBarriersCardProps {
  shouldBlink?: boolean;
}

const SDOHBarriersCard: React.FC<SDOHBarriersCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("SDOH_BARRIERS");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: sdohBarriers,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<SDOHBarriersResponse>({
    endpoint: routes.cards.sdohBarriers,
    memberId,
    processData: (rawData: unknown) =>
      (rawData as { data: SDOHBarriersResponse }).data,
  });

  const getBarriers = () => {
    if (!sdohBarriers) return [];

    return Object.entries(sdohBarriers.barriers) as [
      keyof SDOHBarriersData,
      boolean,
    ][];
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <div className='w-4.5 h-4.5'>
                <img
                  src={SDOHIconLight}
                  alt='SDOH Light'
                  className='block dark:hidden'
                />
                <img
                  src={SDOHIconDark}
                  alt='SDOH Dark'
                  className='hidden dark:block'
                />
              </div>
            </BorderedIconWrapper>
          }
          title='Health Related Social Needs'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 md:h-[287px] lg:h-[492px] gap-[12px] overflow-hidden'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={3} showProfile={false} />;
            }
            if (error) {
              return (
                <div className='flex items-center justify-center h-full md:h-[604px] lg:h-[483px]'>
                  <Typography
                    variant='medium-14'
                    className={`${
                      isErrorRed
                        ? "text-red-500 dark:text-red-400"
                        : "text-grey-600 dark:text-grey-500"
                    } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }
            if (!sdohBarriers) {
              return null;
            }
            return (
              <div
                className='columns-1 md:columns-2 gap-[10px] h-full'
                style={{ columnFill: "auto" }}>
                {getBarriers().map(([key, value]) => {
                const barrierKey = key;
                const label = barrierLabels[barrierKey];
                const isActive = value === true;

                const displayLabel =
                  barrierKey === "educationLiteracy" ? (
                    <>
                      Education/
                      <br className='md:hidden' />
                      <span className='hidden md:inline'> </span>
                      Literacy
                    </>
                  ) : (
                    label
                  );

                return (
                  <div
                    key={key}
                    className={`
                      flex items-center justify-between gap-2
                      px-4 py-3 rounded-lg mb-[10px]
                      border border-grey-300 dark:border-grey-600
                      break-inside-avoid
                    `}>
                    <TruncatedTextTooltip
                      text={label}
                      className='flex-1 min-w-0'>
                      <Typography
                        variant='medium-14'
                        className={`
                          truncate
                          ${
                            isActive
                              ? "text-grey-700 dark:text-grey-400"
                              : "text-grey-500 dark:text-grey-700"
                          }
                        `}>
                        {displayLabel}
                      </Typography>
                    </TruncatedTextTooltip>

                    {isActive ? (
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='flex-shrink-0 ml-2'>
                        <circle cx='10' cy='10' r='10' fill='#4F5B66' />
                        <path
                          d='M14.5 7L8.5 13L5.5 10'
                          className='stroke-white dark:stroke-black'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <Typography
                        variant='medium-14'
                        className='text-grey-500 dark:text-grey-700 ml-2 flex-shrink-0'>
                        --
                      </Typography>
                    )}
                  </div>
                );
                })}
              </div>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default SDOHBarriersCard;
